/**
 * Offer-in-Compromise API layer (milestone 03-14). Follows the settled envelope
 * convention: `apiFetch` returns the raw `{ data }` envelope; this layer unwraps
 * `data` and maps snake_case↔camelCase. All money crosses the wire as integer
 * cents. The flow record + orchestration endpoints are the only new surface;
 * worksheet/offer/form endpoints are reused from Tiers 2.2/3.
 */

import { apiFetch } from "@/lib/api";
import type {
  OICBasis,
  OICFlow,
  OICStep,
  OICSummary,
  OfferPlan,
  PacketResult,
} from "@/features/oic/types";

interface Envelope<T> {
  data: T;
}

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

const toCamel = (s: string) => s.replace(/_([a-z0-9])/g, (_, c) => c.toUpperCase());
const toSnake = (s: string) => s.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);

function rename<T>(value: unknown, fn: (k: string) => string): T {
  if (Array.isArray(value)) return value.map((v) => rename(v, fn)) as T;
  if (isPlainObject(value)) {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) out[fn(k)] = rename(v, fn);
    return out as T;
  }
  return value as T;
}

const camelize = <T>(v: unknown): T => rename<T>(v, toCamel);
const decamelize = (v: unknown): unknown => rename(v, toSnake);

const base = (caseId: string) => `/cases/${caseId}/oic/`;

/** GET — fetch-or-create the case's OIC flow (idempotent singleton). */
export async function getOrCreateFlow(caseId: string): Promise<OICFlow> {
  const res = await apiFetch<Envelope<unknown>>(base(caseId));
  return camelize<OICFlow>(res.data);
}

export interface FlowPatch {
  step?: OICStep;
  offerPlan?: OfferPlan | null;
  offerAmountCents?: number;
  basis?: OICBasis;
  eligibilityAck?: boolean;
  eligibilityNote?: string;
  fullPayOverride?: boolean;
}

export async function patchFlow(caseId: string, patch: FlowPatch): Promise<OICFlow> {
  const res = await apiFetch<Envelope<unknown>>(base(caseId), {
    method: "PATCH",
    json: decamelize(patch) as Record<string, unknown>,
  });
  return camelize<OICFlow>(res.data);
}

/** GET — assembled RCP summary (reads the Tier 2.2 offer service; no re-derive). */
export async function getSummary(caseId: string): Promise<OICSummary> {
  const res = await apiFetch<Envelope<unknown>>(`${base(caseId)}summary/`);
  return camelize<OICSummary>(res.data);
}

/** POST — recompute + snapshot the RCP audit cache; returns flow + summary. */
export async function recompute(
  caseId: string,
): Promise<{ flow: OICFlow; summary: OICSummary }> {
  const res = await apiFetch<Envelope<{ flow: unknown; summary: unknown }>>(
    `${base(caseId)}recompute/`,
    { method: "POST" },
  );
  return {
    flow: camelize<OICFlow>(res.data.flow),
    summary: camelize<OICSummary>(res.data.summary),
  };
}

/** POST — record the eligibility-gate acknowledgement + note + full-pay override. */
export async function acknowledgeEligibility(
  caseId: string,
  patch: Pick<FlowPatch, "eligibilityAck" | "eligibilityNote" | "fullPayOverride" | "step">,
): Promise<OICFlow> {
  const res = await apiFetch<Envelope<unknown>>(`${base(caseId)}acknowledge/`, {
    method: "POST",
    json: decamelize(patch) as Record<string, unknown>,
  });
  return camelize<OICFlow>(res.data);
}

/** POST — generate + attach the OIC packet (656 seam + 433-A/B(OIC)). */
export async function generatePacket(
  caseId: string,
): Promise<{ flow: OICFlow; packet: PacketResult }> {
  const res = await apiFetch<Envelope<{ flow: unknown; packet: unknown }>>(
    `${base(caseId)}packet/generate/`,
    { method: "POST" },
  );
  return {
    flow: camelize<OICFlow>(res.data.flow),
    packet: camelize<PacketResult>(res.data.packet),
  };
}

export interface SubmitPayload {
  submittedOn: string;
  irsOfferNumber?: string;
  transitionCase?: boolean;
}

/** POST — mark the OIC filed (records submission + advances the case). */
export async function submitOffer(
  caseId: string,
  payload: SubmitPayload,
): Promise<OICFlow> {
  const res = await apiFetch<Envelope<unknown>>(`${base(caseId)}submit/`, {
    method: "POST",
    json: decamelize(payload) as Record<string, unknown>,
  });
  return camelize<OICFlow>(res.data);
}
